'use client';

import UserDropDown from '@/components/common/UserDropDown';
import { Separator } from '@/components/ui/separator';
import useNotify from '@/hooks/useNotify';
import { openWindow } from '@/redux/slice/windowSlice';
import { MenuItem } from '@/utils/types/header';
import { WinDataType } from '@/utils/types/window';
import Image from 'next/image';
import Link from 'next/link';
import { memo, MouseEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useMenuFormPermissions from '../permissions/useMenuFormPermissions';
import Notification from './Notification';
const Header = () => {
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [submenuIndex, setSubmenuIndex] = useState<null | number>(null);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const {
    menuItems,
    isLoading
  } = useMenuFormPermissions();
  const {
    triggerNotify,
    NotifyComponent
  } = useNotify();
  const navbarRef = useRef<HTMLDivElement | null>(null);
  const handleNavbarToggle = () => {
    setIsNavbarOpen(prevState => !prevState);
  };
  const handleOpenCreditForm = () => {
    const modelTitle = 'Pending Credit, Price & Bad Debt Write Off and Order Verification';
    dispatch(openWindow({
      title: modelTitle,
      componentKey: 'credit',
      id: null,
      winData: {
        tabName: 'buyoutQuote'
      }
    }));
  };
  const handleMenuClick = (title: string, componentKey: string, size?: {
    width: number;
    height: number;
  }, isAdjustable?: boolean, winData?: WinDataType) => {
    if (isNavbarOpen) {
      handleNavbarToggle();
    }
    if (componentKey === 'rentalConversion') {
      triggerNotify({
        notifyType: 'none',
        notifyMessage: 'The old Rental Conversion form has been retired. Please use the Buyout Quote Approval system.',
        onYes: () => {
          handleOpenCreditForm();
        },
        onClose: () => {
          handleOpenCreditForm();
        }
      });
      return;
    }
    dispatch(openWindow({
      title,
      componentKey,
      id: null,
      size,
      isAdjustable: isAdjustable,
      winData
    }));
  };
  const toggleDropdown = (index: number) => {
    if (submenuIndex === index && isDropdownOpen) {
      setIsDropdownOpen(false);
      setSubmenuIndex(null);
    } else {
      setIsDropdownOpen(true);
      setSubmenuIndex(index);
    }
  };
  const closeAll = () => {
    setIsNavbarOpen(false);
    setIsDropdownOpen(false);
    setSubmenuIndex(null);
    setIsNotificationOpen(false);
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
      closeAll();
    }
  };
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => handleClickOutside(event);
    document.addEventListener('mousedown', handleDocumentClick as unknown as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick as unknown as EventListener);
    };
  }, []);
  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setSubmenuIndex(null);
  };
  function handleItemClick(index: SetStateAction<number | null>) {
    setIsDropdownOpen(!isDropdownOpen || submenuIndex !== index);
    setSubmenuIndex(index);
  }
  const handleItemMouseOver = (e: React.MouseEvent | React.FocusEvent, item: MenuItem, index: number) => {
    if (item.submenu && isDropdownOpen) {
      e.preventDefault();
      toggleDropdown(index);
    } else {
      closeDropdown();
    }
  };
  return <div className="relative z-[70] flex items-center justify-between bg-[#FFFFFF2E] px-4 py-1 backdrop-blur-[50px] backdrop-filter" ref={navbarRef} data-sentry-component="Header" data-sentry-source-file="Header.tsx">
      <nav>
        <div className="flex flex-wrap items-center">
          <Link href="#" className="flex items-center space-x-3 rtl:space-x-reverse" data-sentry-element="Link" data-sentry-source-file="Header.tsx">
            <Image src="/images/MG_Logo_NextStar_White.png" width={72} height={48} alt="TRS" className="m-auto !h-[40px] w-auto" data-sentry-element="Image" data-sentry-source-file="Header.tsx" />
          </Link>
          <button data-collapse-toggle="navbar-default" type="button" className="ml-3 inline-flex h-10 w-10 items-center justify-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 xl:hidden" aria-controls="navbar-default" aria-expanded={isNavbarOpen ? 'true' : 'false'} onClick={handleNavbarToggle}>
            <span className="sr-only">Open main menu</span>
            <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 14" data-sentry-element="svg" data-sentry-source-file="Header.tsx">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1h15M1 7h15M1 13h15" data-sentry-element="path" data-sentry-source-file="Header.tsx" />
            </svg>
          </button>
          <div className={`${isNavbarOpen ? 'max-h-auto block xl:max-h-[unset]' : 'hidden max-h-0 xl:max-h-[unset]'} duration-2000 absolute left-0 top-[100%] w-full overflow-hidden pt-0 transition-[max-height,opacity] ease-in-out lg:overflow-visible xl:relative xl:left-[unset] xl:top-[unset] xl:block xl:w-auto xl:p-0`} id="navbar-default">
            <ul className="flex flex-col border border-gray-100 bg-gray-50 p-4 font-medium md:space-x-2 md:border-0 md:p-0 xl:mb-2 xl:ml-4 xl:mt-2 xl:flex-row xl:bg-transparent rtl:space-x-reverse">
              {!isLoading && menuItems.map((item, index) => <li key={item.title} className={`dropdown-menu relative ${isDropdownOpen && submenuIndex === index ? 'bg-[#FFFFFF2E]' : ''}`}>
                    <button type="button" aria-haspopup="true" aria-expanded={isDropdownOpen && submenuIndex === index ? 'true' : 'false'} className="flex w-full cursor-pointer justify-between rounded-[4px] px-2 py-2 text-base leading-[1] text-black hover:bg-gray-100 md:border-0 md:hover:bg-[#FFFFFF2E] xl:text-xs xl:text-white xl:hover:text-white 2xl:px-3" onClick={() => handleItemClick(index)} onMouseOver={e => handleItemMouseOver(e, item, index)} onFocus={e => handleItemMouseOver(e, item, index)}>
                      {item.title}
                      {item.submenu && <svg className="xl:hidden" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                        </svg>}
                    </button>
                    {item.submenu && submenuIndex === index && isDropdownOpen && <div className="w-full divide-y divide-gray-100 rounded-lg bg-white font-normal shadow md:w-auto xl:absolute">
                        <ul className="my-2 max-h-[calc(100vh-130px)] cursor-pointer overflow-y-auto px-4 py-3 text-sm text-gray-700 transition-colors">
                          {item.submenu.map(submenuItem => <li key={submenuItem?.title}>
                              {submenuItem.divider ? <Separator className="hidden md:block" /> : <button className="w-full cursor-pointer whitespace-nowrap px-4 py-1 text-start font-medium hover:bg-[#91C9F7]" onClick={() => {
                      handleMenuClick(submenuItem?.windowTitle ?? submenuItem?.title ?? 'default', submenuItem?.module ?? 'default', submenuItem?.size, submenuItem?.isAdjustable, submenuItem?.winData);
                      closeDropdown();
                    }}>
                                  {submenuItem.title}
                                </button>}
                            </li>)}
                        </ul>
                      </div>}
                  </li>)}
            </ul>
          </div>
        </div>
      </nav>
      {NotifyComponent}

      <div className="flex items-center gap-x-0">
        <Notification isNotificationOpen={isNotificationOpen} setIsNotificationOpen={setIsNotificationOpen} data-sentry-element="Notification" data-sentry-source-file="Header.tsx" />
        <UserDropDown data-sentry-element="UserDropDown" data-sentry-source-file="Header.tsx" />
      </div>
    </div>;
};
export default memo(Header);